import { useState } from 'react'
import { format } from 'date-fns'
import classNames from 'classnames'
import Popover from 'components/shared/Popover'
import MiniCalendar from 'components/shared/MiniCalendar'
import TimePicker from 'components/shared/TimePicker'
import { IEventData } from 'interface/calendar'
import { eventConstants } from 'constants/index'

const { timeOptions } = eventConstants

interface IEventTimePickerProps {
  eventData: IEventData;
  setEventData: React.Dispatch<React.SetStateAction<IEventData>>;
  disabled?: boolean;
}

export const EventTimePicker: React.FC<IEventTimePickerProps> = ({
  eventData,
  setEventData,
  disabled = false,
}) => {
  const [isTimePickerOpen, setIsTimePickerOpen] = useState({
    isStartTimePickerOpen: false,
    isEndTimePickerOpen: false,
    isDatePickerOpen: false,
  })

  const handleChangeSelectedDate = (newDate: Date) => {
    setEventData((prevData) => ({ ...prevData, selectedDate: newDate }))
    toggleDatePickerShown()
  }

  const toggleDatePickerShown = () => {
    setIsTimePickerOpen((prevValue) => ({
      ...prevValue,
      isDatePickerOpen: !prevValue.isDatePickerOpen,
    }))
  }

  const toggleStartTimePickerShown = () => {
    setIsTimePickerOpen((prevValue) => ({
      ...prevValue,
      isStartTimePickerOpen: !prevValue.isStartTimePickerOpen,
    }))
  }

  const toggleEndTimePickerShown = () => {
    setIsTimePickerOpen((prevValue) => ({
      ...prevValue,
      isEndTimePickerOpen: !prevValue.isEndTimePickerOpen,
    }))
  }

  const handleChangeSelectedStartTime = (time: string) => {
    setEventData((prevData) => ({ ...prevData, selectedStartTime: time }))
    setIsTimePickerOpen((prevValue) => ({
      ...prevValue,
      isStartTimePickerOpen: false,
    }))
    const startTimeIndex = timeOptions.indexOf(time)
    const endTimeIndex = timeOptions.indexOf(eventData.selectedEndTime)
    if (startTimeIndex >= endTimeIndex)
      setEventData((prevData) => ({
        ...prevData,
        selectedEndTime: timeOptions[startTimeIndex + 1],
      }))
  }

  const handleChangeSelectedEndTime = (time: string) => {
    setEventData((prevData) => ({ ...prevData, selectedEndTime: time }))
    setIsTimePickerOpen((prevValue) => ({
      ...prevValue,
      isEndTimePickerOpen: false,
    }))
    const startTimeIndex = timeOptions.indexOf(eventData.selectedStartTime)
    const endTimeIndex = timeOptions.indexOf(time)
    if (startTimeIndex >= endTimeIndex)
      setEventData((prevData) => ({
        ...prevData,
        selectedStartTime: timeOptions[endTimeIndex - 1],
      }))
  }

  return (
    <div>
      <p className="font-inter text-sm font-normal leading-4 text-dark-gray-100">
        When
      </p>
      <div className="mt-1">
        <div className="flex justify-between gap-2 [&>div:first-child]:grow">
          <Popover
            open={isTimePickerOpen.isDatePickerOpen}
            content={
              <div
                className="absolute bg-white p-[10px] rounded-lg"
                style={{
                  boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.15)',
                }}
              >
                <MiniCalendar
                  view="month"
                  currentDate={eventData.selectedDate}
                  onChange={handleChangeSelectedDate}
                />
              </div>
            }
            position="bottom"
            onClickOutside={toggleDatePickerShown}
          >
            <div
              className={classNames(
                'relative h-[38px] border rounded-lg py-[6px] px-[12px] font-inter text-sm font-normal leading-4 flex items-center',
                disabled ? 'text-gray-400' : 'text-dark-gray-100  cursor-pointer'
              )}
              onClick={disabled ? () => {} : toggleDatePickerShown}
            >
              <span>{format(eventData.selectedDate, 'EEEE, MMM d')}</span>
            </div>
          </Popover>
          <Popover
            open={isTimePickerOpen.isStartTimePickerOpen}
            content={
              <div
                className="absolute bg-white rounded-lg h-[180px] w-[90px] overflow-auto"
                style={{
                  boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.15)',
                }}
              >
                <TimePicker
                  value={eventData.selectedStartTime}
                  onChange={handleChangeSelectedStartTime}
                  options={timeOptions.slice(0, -1)}
                />
              </div>
            }
            position="bottom"
            onClickOutside={toggleStartTimePickerShown}
          >
            <div
              className={classNames(
                'h-[38px] w-[90px] border rounded-lg py-[6px] px-[12px] font-inter text-sm font-normal leading-4 flex items-center',
                disabled ? 'text-gray-400' : 'text-dark-gray-100  cursor-pointer'
              )}
              onClick={disabled ? () => {} : toggleStartTimePickerShown}
            >
              {eventData.selectedStartTime}
            </div>
          </Popover>
          <Popover
            open={isTimePickerOpen.isEndTimePickerOpen}
            content={
              <div
                className="absolute bg-white rounded-lg h-[180px] w-[90px] overflow-auto"
                style={{
                  boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.15)',
                }}
              >
                <TimePicker
                  value={eventData.selectedEndTime}
                  onChange={handleChangeSelectedEndTime}
                  options={timeOptions.slice(1)}
                />
              </div>
            }
            position="bottom"
            onClickOutside={toggleEndTimePickerShown}
          >
            <div
              className={classNames(
                'h-[38px] w-[90px] border rounded-lg py-[6px] px-[12px] font-inter text-sm font-normal leading-4 flex items-center',
                disabled ? 'text-gray-400' : 'text-dark-gray-100 cursor-pointer'
              )}
              onClick={disabled ? () => {} : toggleEndTimePickerShown}
            >
              {eventData.selectedEndTime}
            </div>
          </Popover>
        </div>
      </div>
    </div>
  )
}
